import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/routes";
import { Activity, ArrowRight, ArrowLeft, Stethoscope, Shield, FileText, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const STEPS = [
  { id: "profile", label: "Provider Profile", icon: Stethoscope },
  { id: "credentials", label: "Credentials", icon: Shield },
  { id: "license", label: "License & DEA", icon: FileText },
  { id: "review", label: "Review & Submit", icon: CheckCircle2 },
];

const SPECIALTIES = [
  "Family Medicine",
  "Internal Medicine",
  "Pediatrics",
  "OB/GYN",
  "Psychiatry",
  "Cardiology",
  "Dermatology",
  "Endocrinology",
  "Gastroenterology",
  "Geriatrics",
  "Neurology",
  "Oncology",
  "Orthopedics",
  "Pulmonology",
  "Rheumatology",
  "Urology",
  "General Practice",
  "Nurse Practitioner",
  "Physician Assistant",
  "Licensed Clinical Social Worker",
  "Other",
];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

interface OnboardingData {
  firstName: string;
  middleName: string;
  lastName: string;
  specialty: string;
  bio: string;
  npiNumber: string;
  taxId: string;
  primaryState: string;
  licenseNumber: string;
  deaNumber: string;
  malpracticeCarrier: string;
  malpracticePolicyNumber: string;
  boardCertification: string;
}

export default function ProviderOnboarding() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [data, setData] = useState<OnboardingData>({
    firstName: "",
    middleName: "",
    lastName: "",
    specialty: "",
    bio: "",
    npiNumber: "",
    taxId: "",
    primaryState: "",
    licenseNumber: "",
    deaNumber: "",
    malpracticeCarrier: "",
    malpracticePolicyNumber: "",
    boardCertification: "",
  });

  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const updateField = (field: keyof OnboardingData, value: string) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return data.firstName.trim() && data.lastName.trim() && data.specialty;
      case 1:
        return data.npiNumber.trim().length === 10 && data.primaryState;
      case 2:
        return data.licenseNumber.trim();
      case 3:
        return true;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    if (!user) return;
    setIsSubmitting(true);

    try {
      const displayName = [data.firstName, data.middleName, data.lastName].filter(Boolean).join(" ").trim();

      // Update profiles with structured names
      await supabase.from("profiles").update({
        first_name: data.firstName.trim(),
        middle_name: data.middleName.trim() || null,
        last_name: data.lastName.trim(),
        display_name: displayName,
      } as any).eq("id", user.id);

      // Insert provider profile
      const { data: provider, error: providerError } = await (supabase as any)
        .from("providers")
        .insert({
          user_id: user.id,
          display_name: displayName,
          specialty: data.specialty,
          bio: data.bio.trim() || null,
        })
        .select("id")
        .single();

      if (providerError) throw providerError;

      // Insert compliance record
      const { error: complianceError } = await (supabase as any)
        .from("provider_compliance")
        .insert({
          provider_id: provider.id,
          npi_number: data.npiNumber.trim(),
          tax_id: data.taxId.trim() || null,
          specialty: data.specialty,
          primary_state: data.primaryState,
          dea_number: data.deaNumber.trim() || null,
          malpractice_insurance_carrier: data.malpracticeCarrier.trim() || null,
          malpractice_policy_number: data.malpracticePolicyNumber.trim() || null,
          board_certification: data.boardCertification.trim() || null,
          overall_status: "pending_review",
        });

      if (complianceError) throw complianceError;

      // Insert license record
      if (data.licenseNumber.trim() && data.primaryState) {
        // First get the compliance id
        const { data: complianceData } = await (supabase as any)
          .from("provider_compliance")
          .select("id")
          .eq("provider_id", provider.id)
          .single();

        if (complianceData) {
          await (supabase as any)
            .from("provider_licenses")
            .insert({
              compliance_id: complianceData.id,
              state: data.primaryState,
              license_number: data.licenseNumber.trim(),
              license_status: "pending_verification",
            });
        }
      }

      toast({
        title: "Onboarding complete!",
        description: "Your credentials have been submitted for review. Welcome to Vivien Health!",
      });

      navigate(ROUTES.PROVIDER);
    } catch (error: any) {
      console.error("Onboarding error:", error);
      toast({
        title: "Onboarding failed",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl gradient-primary shadow-lg">
            <Activity className="h-7 w-7 text-primary-foreground" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground">Vivien Health</h1>
            <p className="text-sm text-muted-foreground">Provider Onboarding</p>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isComplete = index < currentStep;
            return (
              <div key={step.id} className="flex items-center gap-2">
                <div
                  className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : isComplete
                      ? "bg-primary/20 text-primary"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{step.label}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`w-6 h-0.5 ${isComplete ? "bg-primary" : "bg-border"}`} />
                )}
              </div>
            );
          })}
        </div>

        <Card className="border-border/50 shadow-xl">
          {/* Step 0: Provider Profile */}
          {currentStep === 0 && (
            <>
              <CardHeader>
                <CardTitle className="font-display text-xl">Provider Profile</CardTitle>
                <CardDescription>Tell us about yourself and your practice</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      value={data.firstName}
                      onChange={(e) => updateField("firstName", e.target.value)}
                      placeholder="Jane"
                      maxLength={50}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="middleName">Middle Name</Label>
                    <Input
                      id="middleName"
                      value={data.middleName}
                      onChange={(e) => updateField("middleName", e.target.value)}
                      placeholder="M."
                      maxLength={50}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      value={data.lastName}
                      onChange={(e) => updateField("lastName", e.target.value)}
                      placeholder="Smith"
                      maxLength={50}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="specialty">Specialty *</Label>
                  <Select value={data.specialty} onValueChange={(v) => updateField("specialty", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your specialty" />
                    </SelectTrigger>
                    <SelectContent>
                      {SPECIALTIES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bio">Bio (optional)</Label>
                  <Textarea
                    id="bio"
                    value={data.bio}
                    onChange={(e) => updateField("bio", e.target.value)}
                    placeholder="Brief description of your experience and areas of focus..."
                    maxLength={500}
                    rows={3}
                  />
                </div>
              </CardContent>
            </>
          )}

          {/* Step 1: Credentials */}
          {currentStep === 1 && (
            <>
              <CardHeader>
                <CardTitle className="font-display text-xl">Credentials</CardTitle>
                <CardDescription>Enter your NPI and identification details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="npiNumber">NPI Number * (10 digits)</Label>
                  <Input
                    id="npiNumber"
                    value={data.npiNumber}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                      updateField("npiNumber", val);
                    }}
                    placeholder="1234567890"
                    maxLength={10}
                    required
                  />
                  {data.npiNumber && data.npiNumber.length !== 10 && (
                    <p className="text-sm text-destructive">NPI must be exactly 10 digits</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="taxId">Tax ID / EIN (optional)</Label>
                  <Input
                    id="taxId"
                    value={data.taxId}
                    onChange={(e) => updateField("taxId", e.target.value)}
                    placeholder="XX-XXXXXXX"
                    maxLength={20}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="primaryState">Primary Practice State *</Label>
                  <Select value={data.primaryState} onValueChange={(v) => updateField("primaryState", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="boardCertification">Board Certification (optional)</Label>
                  <Input
                    id="boardCertification"
                    value={data.boardCertification}
                    onChange={(e) => updateField("boardCertification", e.target.value)}
                    placeholder="e.g., ABIM - Internal Medicine"
                    maxLength={100}
                  />
                </div>
              </CardContent>
            </>
          )}

          {/* Step 2: License & DEA */}
          {currentStep === 2 && (
            <>
              <CardHeader>
                <CardTitle className="font-display text-xl">License & DEA</CardTitle>
                <CardDescription>Enter your medical license and DEA information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="licenseNumber">Medical License Number *</Label>
                  <Input
                    id="licenseNumber"
                    value={data.licenseNumber}
                    onChange={(e) => updateField("licenseNumber", e.target.value)}
                    placeholder="Your state license number"
                    maxLength={50}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="deaNumber">DEA Number (optional)</Label>
                  <Input
                    id="deaNumber"
                    value={data.deaNumber}
                    onChange={(e) => updateField("deaNumber", e.target.value)}
                    placeholder="e.g., AB1234567"
                    maxLength={20}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="malpracticeCarrier">Malpractice Insurance Carrier (optional)</Label>
                  <Input
                    id="malpracticeCarrier"
                    value={data.malpracticeCarrier}
                    onChange={(e) => updateField("malpracticeCarrier", e.target.value)}
                    placeholder="Insurance carrier name"
                    maxLength={100}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="malpracticePolicyNumber">Policy Number (optional)</Label>
                  <Input
                    id="malpracticePolicyNumber"
                    value={data.malpracticePolicyNumber}
                    onChange={(e) => updateField("malpracticePolicyNumber", e.target.value)}
                    placeholder="Policy number"
                    maxLength={50}
                  />
                </div>
              </CardContent>
            </>
          )}

          {/* Step 3: Review */}
          {currentStep === 3 && (
            <>
              <CardHeader>
                <CardTitle className="font-display text-xl">Review & Submit</CardTitle>
                <CardDescription>Please review your information before submitting</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ReviewField label="Name" value={[data.firstName, data.middleName, data.lastName].filter(Boolean).join(" ")} />
                  <ReviewField label="Specialty" value={data.specialty} />
                  <ReviewField label="NPI Number" value={data.npiNumber} />
                  <ReviewField label="Primary State" value={data.primaryState} />
                  <ReviewField label="License Number" value={data.licenseNumber} />
                  <ReviewField label="DEA Number" value={data.deaNumber || "Not provided"} />
                  <ReviewField label="Tax ID" value={data.taxId || "Not provided"} />
                  <ReviewField label="Board Certification" value={data.boardCertification || "Not provided"} />
                  <ReviewField label="Malpractice Carrier" value={data.malpracticeCarrier || "Not provided"} />
                  <ReviewField label="Policy Number" value={data.malpracticePolicyNumber || "Not provided"} />
                </div>
                {data.bio && (
                  <div className="pt-2">
                    <ReviewField label="Bio" value={data.bio} />
                  </div>
                )}
                <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    By submitting, you confirm that the information provided is accurate. Your credentials will be reviewed by our compliance team.
                  </p>
                </div>
              </CardContent>
            </>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between p-6 pt-0">
            <Button
              variant="outline"
              onClick={() => setCurrentStep((s) => s - 1)}
              disabled={currentStep === 0}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            {currentStep < STEPS.length - 1 ? (
              <Button
                onClick={() => setCurrentStep((s) => s + 1)}
                disabled={!canProceed()}
                className="gradient-primary"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="gradient-primary"
              >
                {isSubmitting ? "Submitting..." : "Complete Onboarding"}
                <CheckCircle2 className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function ReviewField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium text-foreground">{value}</p>
    </div>
  );
}
